import {
  LAYOUTS,
  MARCADORES_POR_FOLHA,
  ROTULO_DO_ESTADO_DO_TRABALHO,
  type EstadoDoTrabalhoDeImpressao,
  type FormatoDeTrabalho,
  type LayoutId,
} from "@foto/shared";
import { comVerbo, horaRelativa } from "./tempo.js";
import type { TomDoSelo } from "./impressora.js";

/**
 * A fila de impressão na tela, sem React: o que cada trabalho diz, em que ordem aparecem, e quando vale
 * consultar o servidor de novo.
 *
 * Regra que atravessa o arquivo: **a tela só diz que a folha saiu quando o trabalho está `concluido`**. Até
 * lá o que se sabe é que ela foi enviada, está esperando ou está sendo impressa — e papel e fita custam
 * caro demais para o operador acreditar numa folha que ainda não saiu.
 */

/** O que a tela precisa de um trabalho; é o que `impressao.trabalhos` devolve. */
export interface TrabalhoDaFila {
  id: number;
  impressoraNome: string;
  layout: string;
  fotos: number;
  copias: number;
  /** 0 até começar; depois, a cópia que está sendo enviada ou impressa. */
  copiaAtual: number;
  estado: EstadoDoTrabalhoDeImpressao;
  detalhe: string | null;
  createdAt: string;
  iniciadoEm: string | null;
  concluidoEm: string | null;
}

export const ESTADOS_ABERTOS: readonly EstadoDoTrabalhoDeImpressao[] = ["aguardando", "imprimindo"];

export const estaAberto = (t: Pick<TrabalhoDaFila, "estado">): boolean =>
  ESTADOS_ABERTOS.includes(t.estado);

// ─── Polling ──────────────────────────────────────────────────────────────────

/** De quanto em quanto tempo se pergunta como vai a fila, enquanto há trabalho aberto. */
export const INTERVALO_DA_FILA_MS = 3_000;

/**
 * Quando consultar `trabalhos` de novo. O worker não avisa por socket, então a tela pergunta — mas só
 * enquanto há trabalho `aguardando` ou `imprimindo`: com a fila parada, perguntar a cada três segundos
 * seria gastar dados do celular e do servidor à toa.
 */
export function intervaloDaFila(
  trabalhos: readonly Pick<TrabalhoDaFila, "estado">[] | undefined,
): number | false {
  return trabalhos?.some(estaAberto) ? INTERVALO_DA_FILA_MS : false;
}

// ─── Ordem ────────────────────────────────────────────────────────────────────

const instante = (iso: string | null): number => (iso ? new Date(iso).getTime() : 0);

/** Quando o trabalho acabou, ou, na falta, quando foi enviado. */
const fimOuEnvio = (t: TrabalhoDaFila): number => instante(t.concluidoEm) || instante(t.createdAt);

export interface FilaOrganizada {
  /** Esperando ou imprimindo: o que o operador acompanha. O que está imprimindo vem primeiro. */
  abertos: TrabalhoDaFila[];
  /** Os últimos que terminaram, do mais recente. */
  recentes: TrabalhoDaFila[];
  /** O resto do histórico, atrás de um "Ver anteriores". */
  anteriores: TrabalhoDaFila[];
}

/**
 * Separa a fila em o que está em andamento e o que já terminou.
 *
 * Os abertos saem na ordem em que a impressora os atende: o que está imprimindo, e depois os que esperam
 * **do mais antigo ao mais novo** (a fila é de uma impressão por vez, em ordem de chegada). Os que
 * terminaram vão do mais recente ao mais antigo, e só os primeiros ficam à vista: o histórico completo é
 * ruído para quem só quer saber se a última folha saiu.
 */
export function organizarFila(trabalhos: readonly TrabalhoDaFila[], aVista = 3): FilaOrganizada {
  const abertos = trabalhos
    .filter(estaAberto)
    .sort(
      (a, b) =>
        Number(b.estado === "imprimindo") - Number(a.estado === "imprimindo") ||
        instante(a.createdAt) - instante(b.createdAt) ||
        a.id - b.id,
    );

  const terminados = trabalhos
    .filter((t) => !estaAberto(t))
    .sort((a, b) => fimOuEnvio(b) - fimOuEnvio(a) || b.id - a.id);

  return { abertos, recentes: terminados.slice(0, aVista), anteriores: terminados.slice(aVista) };
}

// ─── O que cada trabalho diz ──────────────────────────────────────────────────

export interface LinhaDoTrabalho {
  titulo: string;
  selo: { tom: TomDoSelo; rotulo: string };
  /** "Cópia 1 de 2", enquanto imprime. */
  progresso: string | null;
  /** A frase do servidor sobre o que está acontecendo ou deu errado, como veio. */
  detalhe: string | null;
  /** O que fazer, quando falhou. */
  dica: string | null;
  quando: string;
  podeCancelar: boolean;
  /** O que o cancelamento deixa de fazer — só em quem já está na impressora. */
  avisoDoCancelar: string | null;
  /** O trabalho terminou: a tela o mostra mais discreto. */
  terminado: boolean;
}

const TONS: Record<EstadoDoTrabalhoDeImpressao, TomDoSelo> = {
  // Violeta é "o que a máquina está fazendo": esperar a vez também é trabalho dela.
  aguardando: "projecao",
  imprimindo: "projecao",
  concluido: "fixado",
  falhou: "descartar",
  cancelado: "quieto",
};

export function copiasPorExtenso(n: number): string {
  return `${n} ${n === 1 ? "cópia" : "cópias"}`;
}

/**
 * O formato do trabalho dos marcadores, como o servidor o grava em `layout`. É o único que não é um
 * `LayoutId`: a folha vem pronta do conjunto, sem espaços para o operador escolher.
 */
export const FORMATO_DOS_MARCADORES = "marcadores" satisfies FormatoDeTrabalho;

/**
 * O formato de um trabalho, em palavras: "4 por folha" para as folhas montadas na tela de Impressão e
 * "Marcadores, 3 por folha" para a de marcadores — e, quando uma faixa saiu sem arte, "Marcadores, 2 de 3
 * faixas", porque é isso que vai sair no papel. `null` para o formato que a tela não conhece: a linha
 * continua de pé sem ele.
 *
 * `fotos` só conta nos marcadores (nas outras folhas o formato já diz quantos espaços há); sem ele, a
 * folha de marcadores é a inteira.
 */
export function descreverFormato(layout: string, fotos?: number): string | null {
  if (layout === FORMATO_DOS_MARCADORES) {
    const comArte = Math.min(MARCADORES_POR_FOLHA, Math.max(0, fotos ?? MARCADORES_POR_FOLHA));
    return comArte === MARCADORES_POR_FOLHA
      ? `Marcadores, ${MARCADORES_POR_FOLHA} por folha`
      : `Marcadores, ${comArte} de ${MARCADORES_POR_FOLHA} faixas`;
  }
  if (!(layout in LAYOUTS)) return null;
  const l = LAYOUTS[layout as LayoutId];
  // A folha mista (#115) não é "5 por folha": o nome dela diz o que leva.
  return l.tipos?.includes("marcador") ? l.nome : `${l.celulas.length} por folha`;
}

/** "2 cópias · 4 por folha · Sala A": o que foi pedido, sem o número do trabalho, que não diz nada a quem opera. */
export function tituloDoTrabalho(
  t: Pick<TrabalhoDaFila, "copias" | "layout" | "impressoraNome"> & { fotos?: number },
): string {
  return [copiasPorExtenso(t.copias), descreverFormato(t.layout, t.fotos), t.impressoraNome]
    .filter(Boolean)
    .join(" · ");
}

/** A falha já manda para o app da Canon (é a frase da rede)? Então a dica não se repete. */
function jaMencionaOApp(detalhe: string | null): boolean {
  return detalhe !== null && /app da canon/i.test(detalhe);
}

export function descreverTrabalho(t: TrabalhoDaFila, agora: Date): LinhaDoTrabalho {
  const aberto = estaAberto(t);
  const copia = Math.min(t.copias, Math.max(1, t.copiaAtual));

  return {
    titulo: tituloDoTrabalho(t),
    selo: { tom: TONS[t.estado], rotulo: ROTULO_DO_ESTADO_DO_TRABALHO[t.estado] },
    progresso: t.estado === "imprimindo" ? `Cópia ${copia} de ${t.copias}` : null,
    detalhe: t.detalhe,
    dica:
      t.estado === "falhou" && !jaMencionaOApp(t.detalhe)
        ? "Baixe a folha e imprima pelo app da Canon."
        : null,
    quando: aberto
      ? comVerbo("Enviada", t.createdAt, agora)
      : horaRelativa(t.concluidoEm ?? t.createdAt, agora),
    podeCancelar: aberto,
    avisoDoCancelar:
      t.estado === "imprimindo"
        ? t.copias > 1
          ? "A folha que já está sendo impressa termina; as cópias que faltam não saem."
          : "A folha que já está sendo impressa termina mesmo assim."
        : null,
    terminado: !aberto,
  };
}

/**
 * A frase que abre o painel depois de enviar, conforme o trabalho anda. Enquanto está aberto, diz o que
 * acontece com a folha e onde acompanhar; só quando termina diz o resultado — e é aí, e só aí, que se
 * pode falar de impressão feita.
 */
export function fraseDoPainelEnviado(estado: EstadoDoTrabalhoDeImpressao): string {
  switch (estado) {
    case "aguardando":
    case "imprimindo":
      return "A folha entrou na fila. Pode fechar: o andamento continua na página, em “Fila de impressão”.";
    case "concluido":
      return "A impressora terminou a impressão.";
    case "falhou":
      return "A impressão não deu certo. O motivo está abaixo.";
    case "cancelado":
      return "A impressão foi cancelada.";
  }
}

// ─── Aviso para leitor de tela ────────────────────────────────────────────────

function fraseDoEstado(t: TrabalhoDaFila): string {
  switch (t.estado) {
    case "aguardando":
      return "aguardando a impressora";
    case "imprimindo":
      return `imprimindo, cópia ${Math.min(t.copias, Math.max(1, t.copiaAtual))} de ${t.copias}`;
    case "concluido":
      return "concluída";
    case "falhou":
      return "falhou: veja o motivo na fila de impressão";
    case "cancelado":
      return "cancelada";
  }
}

/**
 * O que um leitor de tela deve anunciar quando a fila muda. Só o que **mudou** entre duas leituras — um
 * trabalho novo, um que trocou de estado, ou de cópia —, para o `aria-live` não repetir a lista inteira
 * a cada três segundos. `null` quando nada mudou.
 */
export function anuncioDaMudanca(
  antes: readonly TrabalhoDaFila[],
  depois: readonly TrabalhoDaFila[],
): string | null {
  const antigos = new Map(antes.map((t) => [t.id, t]));
  const frases: string[] = [];

  for (const t of depois) {
    const anterior = antigos.get(t.id);
    const mudou = !anterior || anterior.estado !== t.estado || anterior.copiaAtual !== t.copiaAtual;
    if (mudou) frases.push(`Impressão em ${t.impressoraNome}: ${fraseDoEstado(t)}.`);
  }
  return frases.length > 0 ? frases.join(" ") : null;
}
